import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import AvatarUploader from "@/components/AvatarUploader";
import MediaLightbox from "@/components/MediaLightbox";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowUpRight, SignOut, FloppyDisk, Heart, CameraPlus, X as CloseIcon } from "@phosphor-icons/react";

export default function Account() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (!user) {
    navigate("/", { replace: true });
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar active="account" />
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-foreground/60">Account</div>
        <h1 className="font-display text-4xl tracking-tighter mb-8">Your profile</h1>

        <PhotoNudge user={user} />

        <section className="border-2 border-foreground p-6 bg-card" id="profile-photo-section">
          <h2 className="font-display text-2xl mb-4">Profile photo</h2>
          <AvatarUploader size={112} />
        </section>

        <section className="mt-6 border-2 border-foreground p-6 bg-card">
          <h2 className="font-display text-2xl mb-4">Identity</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <Field label="Name" value={user.name} />
            <Field label="Email" value={user.email} />
            <Field label="Phone" value={user.phone || "—"} />
            <Field label="Role" value={user.role || "—"} mono />
            <Field
              label="Verified"
              value={user.verified ? "YES" : "No"}
              accent={user.verified}
            />
            <Field label="Joined" value={(user.created_at || "").slice(0, 10)} mono />
          </div>
          {user.role === "driver" && (
            <div className="mt-6">
              <Link
                to="/driver/profile"
                data-testid="account-edit-driver-profile"
                className="font-mono text-[10px] uppercase tracking-wider underline inline-flex items-center gap-1"
              >
                Edit driver bio & gallery <ArrowUpRight size={12} />
              </Link>
            </div>
          )}
        </section>

        {user.role === "rider" && <RiderProfileSection user={user} />}

        <section className="mt-6 border-2 border-foreground p-6 bg-card">
          <h2 className="font-display text-2xl mb-2">Session</h2>
          <p className="text-foreground/70 text-sm mb-4">
            Signed in with Google as <span className="font-mono">{user.email}</span>.
          </p>
          <Button
            data-testid="account-logout-btn"
            variant="outline"
            onClick={logout}
            className="rounded-none border-2 border-foreground"
          >
            <SignOut size={16} /> Sign out
          </Button>
        </section>
      </div>
    </div>
  );
}

function RiderProfileSection({ user }) {
  const { refresh } = useAuth();
  const [form, setForm] = useState({
    bio: user.bio || "",
    pickup_note: user.pickup_note || "",
    emergency_name: user.emergency_name || "",
    emergency_phone: user.emergency_phone || "",
    accessibility: user.accessibility || "",
  });
  const [saving, setSaving] = useState(false);

  // Keep form in sync if the auth user refreshes (e.g. after first login)
  useEffect(() => {
    setForm({
      bio: user.bio || "",
      pickup_note: user.pickup_note || "",
      emergency_name: user.emergency_name || "",
      emergency_phone: user.emergency_phone || "",
      accessibility: user.accessibility || "",
    });
  }, [user.bio, user.pickup_note, user.emergency_name, user.emergency_phone, user.accessibility]);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/users/rider-profile", form);
      toast.success("Profile saved · drivers will see this on pickup");
      if (refresh) await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mt-6 border-2 border-foreground p-6 bg-card" data-testid="rider-profile-section">
      <h2 className="font-display text-2xl mb-1">Rider profile</h2>
      <p className="text-foreground/70 text-sm mb-5">
        Help your driver recognise you. Everything here is shown on the driver's pickup card.
      </p>
      <div className="space-y-4">
        <div>
          <Label className="font-mono text-[10px] uppercase tracking-wider">Short bio</Label>
          <Textarea
            data-testid="rider-bio-input"
            value={form.bio}
            onChange={change("bio")}
            placeholder="Hi! I'm a UG student going home most evenings — cash, exact change."
            maxLength={240}
            rows={2}
            className="mt-1 rounded-none border-2 border-foreground"
          />
          <div className="font-mono text-[10px] text-foreground/50 mt-1">{form.bio.length}/240</div>
        </div>

        <div>
          <Label className="font-mono text-[10px] uppercase tracking-wider">Pickup note</Label>
          <Input
            data-testid="rider-pickup-note-input"
            value={form.pickup_note}
            onChange={change("pickup_note")}
            placeholder="Blue gate beside the corner shop · I'll be in a red jacket"
            maxLength={160}
            className="mt-1 rounded-none border-2 border-foreground"
          />
          <div className="font-mono text-[10px] text-foreground/50 mt-1">{form.pickup_note.length}/160</div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-wider">Emergency contact name</Label>
            <Input
              data-testid="rider-emergency-name-input"
              value={form.emergency_name}
              onChange={change("emergency_name")}
              placeholder="Mom · Sis · Roommate"
              maxLength={80}
              className="mt-1 rounded-none border-2 border-foreground"
            />
          </div>
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-wider">Emergency contact phone</Label>
            <Input
              data-testid="rider-emergency-phone-input"
              value={form.emergency_phone}
              onChange={change("emergency_phone")}
              placeholder="+592 6xx xxxx"
              maxLength={24}
              className="mt-1 rounded-none border-2 border-foreground"
            />
          </div>
        </div>

        <div>
          <Label className="font-mono text-[10px] uppercase tracking-wider">Accessibility needs</Label>
          <Input
            data-testid="rider-accessibility-input"
            value={form.accessibility}
            onChange={change("accessibility")}
            placeholder="Wheelchair, child seat, service animal, etc."
            maxLength={160}
            className="mt-1 rounded-none border-2 border-foreground"
          />
        </div>

        <Button
          data-testid="rider-profile-save-btn"
          onClick={save}
          disabled={saving}
          className="rounded-none bg-foreground text-background hover:bg-foreground/90"
        >
          <FloppyDisk size={16} weight="bold" /> {saving ? "Saving…" : "Save profile"}
        </Button>
      </div>
      <RiderMediaGallery />
    </section>
  );
}


// ---------- Rider media gallery (photos + videos) ----------
function RiderMediaGallery() {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users/media");
      setItems(data || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error("Max file size is 25MB");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/users/media", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setItems((prev) => [data, ...prev]);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (file_id) => {
    if (!confirm("Delete this item?")) return;
    try {
      await api.delete(`/users/media/${file_id}`);
      setItems((prev) => prev.filter((m) => m.file_id !== file_id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const saveCaption = async (file_id, caption) => {
    try {
      await api.patch(`/users/media/${file_id}`, { caption });
      setItems((prev) => prev.map((m) => (m.file_id === file_id ? { ...m, caption } : m)));
      toast.success("Caption saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const mediaUrl = (p) => `${API_URL}/api/public/media/${p}`;

  return (
    <div className="mt-6 pt-6 border-t-2 border-foreground/20">
      <div className="flex items-end justify-between gap-3 mb-3">
        <div>
          <h3 className="font-display text-xl">Photos &amp; videos</h3>
          <p className="text-xs text-foreground/60 mt-0.5">
            Shows on your public profile at <code>/r/{"{your id}"}</code>. Max 25MB per file.
          </p>
        </div>
        <label
          data-testid="rider-media-upload-btn"
          className="shrink-0 inline-flex items-center gap-1.5 px-3 h-10 border-2 border-foreground bg-foreground text-background font-mono text-[11px] uppercase tracking-wider hover:bg-foreground/90 cursor-pointer"
        >
          {uploading ? "Uploading…" : "+ Add media"}
          <input
            type="file"
            accept="image/*,video/mp4,video/webm"
            onChange={pick}
            disabled={uploading}
            className="hidden"
            data-testid="rider-media-file-input"
          />
        </label>
      </div>

      {loading ? (
        <div className="font-mono text-xs text-foreground/60 py-6 text-center">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border-2 border-dashed border-foreground/40 p-6 text-center font-mono text-xs text-foreground/60" data-testid="rider-media-empty">
          No photos or videos yet.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="rider-media-grid">
          {items.map((m, idx) => (
            <div key={m.file_id} className="space-y-1">
            <div className="border-2 border-foreground bg-foreground relative aspect-square overflow-hidden group cursor-pointer" data-testid={`rider-media-${m.file_id}`}>
              <button
                type="button"
                onClick={() => setLightboxIndex(idx)}
                data-testid={`rider-media-open-${m.file_id}`}
                className="absolute inset-0 z-[1] bg-transparent"
                title="Open"
              />
              {m.kind === "rider_media_video" ? (
                <>
                  {m.poster_path ? (
                    <img
                      src={mediaUrl(m.poster_path)}
                      alt=""
                      className="w-full h-full object-cover pointer-events-none"
                    />
                  ) : (
                    <video
                      src={`${mediaUrl(m.storage_path)}#t=0.1`}
                      playsInline
                      muted
                      preload="metadata"
                      className="w-full h-full object-cover bg-foreground pointer-events-none"
                    />
                  )}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-[1]" data-testid={`rider-media-video-overlay-${m.file_id}`}>
                    <div className="w-12 h-12 rounded-full bg-foreground/70 border-2 border-background flex items-center justify-center">
                      <div
                        className="w-0 h-0 ml-1"
                        style={{
                          borderTop: "8px solid transparent",
                          borderBottom: "8px solid transparent",
                          borderLeft: "12px solid #fff",
                        }}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <img src={mediaUrl(m.storage_path)} alt="" className="w-full h-full object-cover" />
              )}
              <button
                onClick={(e) => { e.stopPropagation(); remove(m.file_id); }}
                data-testid={`rider-media-delete-${m.file_id}`}
                className="absolute top-1 right-1 z-[2] w-7 h-7 bg-background border-2 border-foreground inline-flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-destructive hover:text-destructive-foreground transition-opacity"
                title="Delete"
              >
                ×
              </button>
            </div>
              <CaptionEditor
                fileId={m.file_id}
                initial={m.caption || ""}
                onSave={(c) => saveCaption(m.file_id, c)}
              />
              {(m.like_count || 0) > 0 && (
                <div
                  className="flex items-center gap-1 px-0.5 text-xs text-primary"
                  data-testid={`rider-media-likes-${m.file_id}`}
                >
                  <Heart size={12} weight="fill" />
                  <span className="font-mono">{m.like_count}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <MediaLightbox
        items={items}
        index={lightboxIndex}
        mediaUrl={mediaUrl}
        onClose={() => setLightboxIndex(null)}
        onIndexChange={setLightboxIndex}
      />
    </div>
  );
}

function Field({ label, value, mono, accent }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-wider text-foreground/60">{label}</div>
      <div className={`${mono ? "font-mono" : ""} ${accent ? "text-accent font-semibold" : ""}`}>
        {value}
      </div>
    </div>
  );
}

// Dismissible banner that surfaces when the user is still relying on the
// Google-provided avatar (or has none) and hasn't uploaded their own photo.
// Persists dismissal in localStorage so we don't nag them every visit.
function PhotoNudge({ user }) {
  const key = `ridegy_photo_nudge_dismissed_${user.user_id}`;
  const [dismissed, setDismissed] = useState(() => {
    try { return typeof window !== "undefined" && localStorage.getItem(key) === "1"; }
    catch { return false; }
  });
  if (user.has_custom_avatar || dismissed) return null;
  const dismiss = () => {
    try { localStorage.setItem(key, "1"); } catch { /* ignore */ }
    setDismissed(true);
  };
  const scrollTo = () => {
    document.getElementById("profile-photo-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <div
      className="mb-6 border-2 border-foreground bg-secondary p-4 flex items-start gap-3 relative"
      data-testid="account-photo-nudge"
    >
      <CameraPlus size={22} weight="fill" className="shrink-0 mt-0.5 text-accent" />
      <div className="flex-1 min-w-0 pr-6">
        <div className="font-mono text-[10px] uppercase tracking-wider text-foreground/60">
          Add a real photo
        </div>
        <div className="text-sm mt-0.5">
          Drivers and riders recognise you at pickup — a proper headshot builds trust and gets you picked up faster.
        </div>
        <Button
          onClick={scrollTo}
          data-testid="account-photo-nudge-cta"
          className="mt-3 h-9 rounded-none border-2 border-foreground bg-foreground text-background hover:bg-foreground/90"
        >
          Upload photo
        </Button>
      </div>
      <button
        type="button"
        onClick={dismiss}
        data-testid="account-photo-nudge-dismiss"
        className="absolute top-2 right-2 w-7 h-7 border-2 border-foreground bg-background inline-flex items-center justify-center hover:bg-foreground hover:text-background"
        title="Dismiss"
      >
        <CloseIcon size={12} weight="bold" />
      </button>
    </div>
  );
}

function CaptionEditor({ fileId, initial, onSave }) {
  const [value, setValue] = useState(initial || "");
  const [editing, setEditing] = useState(false);
  useEffect(() => { setValue(initial || ""); }, [initial]);
  const commit = async () => {
    setEditing(false);
    if ((value || "").trim() !== (initial || "").trim()) {
      await onSave((value || "").trim());
    }
  };
  if (editing) {
    return (
      <Input
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value.slice(0, 240))}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } }}
        placeholder="Add a caption…"
        maxLength={240}
        className="h-8 rounded-none border-2 border-foreground text-sm"
        data-testid={`rider-media-caption-input-${fileId}`}
      />
    );
  }
  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      data-testid={`rider-media-caption-btn-${fileId}`}
      className="w-full text-left text-xs text-foreground/70 hover:text-foreground truncate px-0.5 min-h-[16px]"
      title="Click to edit caption"
    >
      {value ? value : <span className="text-foreground/40 italic">+ Add caption</span>}
    </button>
  );
}
